// ═══════════════════════════════════════════════════════════
// Contact action helpers: vCard, WhatsApp share, Outlook mail
// ═══════════════════════════════════════════════════════════

import { Department } from "./data";

const ORG_AR = "ديوان الخدمة المدنية والتطوير الحكومي";
const ORG_EN = "Civil Service & Government Development Bureau";

interface ContactInput {
  name: string;          // Full name (preferred Arabic)
  nameEn?: string;       // Latin transliteration if available
  title?: string;        // Job title
  org?: string;          // Department / unit name
  email?: string;
  ext?: string;          // Office phone
  mobile?: string;
  floor?: string;
}

// ─── Phone formatting (Doha = +974) ────────────────────────
function formatQatarPhone(num?: string): string | undefined {
  if (!num || !/^\d+$/.test(num)) return undefined;
  // 8-digit local → prepend country code
  if (num.length === 8) return `+974${num}`;
  if (num.length === 11 && num.startsWith("974")) return `+${num}`;
  return num;
}

// ─── vCard 3.0 export ──────────────────────────────────────
export function buildVCard(c: ContactInput): string {
  const orgFull = c.org ? `${ORG_AR};${c.org}` : ORG_AR;
  const tel1 = formatQatarPhone(c.ext);
  const tel2 = formatQatarPhone(c.mobile);

  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${c.name}`,
    // N: structured (Family;Given;Middle;Prefix;Suffix). We don't parse, leave as one block.
    `N:${c.name};;;;`,
    `ORG:${orgFull}`,
  ];
  if (c.title) lines.push(`TITLE:${c.title}`);
  if (tel1) lines.push(`TEL;TYPE=WORK,VOICE:${tel1}`);
  if (tel2) lines.push(`TEL;TYPE=CELL,VOICE:${tel2}`);
  if (c.email) lines.push(`EMAIL;TYPE=INTERNET,WORK:${c.email}`);
  if (c.nameEn) lines.push(`NOTE:${c.nameEn}${c.floor ? ` — Floor ${c.floor}` : ""}`);
  else if (c.floor) lines.push(`NOTE:Floor ${c.floor}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
}

export function downloadVCard(c: ContactInput) {
  const vcf = buildVCard(c);
  const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  // Strip filesystem-unfriendly punctuation; keep letters (incl. Arabic), digits, spaces, _ -
  const safeName = c.name.replace(/[/\\:*?"<>|.,;!()[\]{}]+/g, "").replace(/\s+/g, "_").slice(0, 40);
  a.href = url;
  a.download = `${safeName || "contact"}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// ─── WhatsApp share ────────────────────────────────────────
export function buildWhatsAppText(c: ContactInput, deepLink?: string): string {
  const parts: string[] = [];
  if (c.org) parts.push(`📍 ${c.org}`);
  parts.push(`👤 ${c.name}`);
  if (c.title) parts.push(c.title);
  if (c.ext) parts.push(`☎️ ${c.ext}`);
  if (c.mobile) parts.push(`📱 ${c.mobile}`);
  if (c.email) parts.push(`✉️ ${c.email}`);
  if (c.floor) parts.push(`🏢 الطابق ${c.floor}`);
  if (deepLink) parts.push(`\n${deepLink}`);
  return parts.join("\n");
}

export function openWhatsApp(c: ContactInput, deepLink?: string) {
  const text = encodeURIComponent(buildWhatsAppText(c, deepLink));
  window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
}

// ─── Outlook Web mailto fallback ───────────────────────────
// Standard mailto: works when an email client is registered.
// Some govt machines force Outlook Web — give them a direct deep-link too.
export function outlookWebUrl(email: string, subject = "", body = ""): string {
  const params = new URLSearchParams({
    to: email,
    subject,
    body,
  });
  return `https://outlook.office.com/mail/deeplink/compose?${params.toString()}`;
}

// ─── Department deep link (used by QR + WhatsApp) ──────────
export function deptDeepLink(dept: Department): string {
  if (typeof window === "undefined") return "";
  const base = `${window.location.protocol}//${window.location.host}`;
  return `${base}/?d=${encodeURIComponent(dept.id)}`;
}
